import React, { useMemo } from 'react'
import get from 'lodash/get'

import Aside, { IList } from './aside'
import ImageRidingLantern from './image-riding-lantern'

import Select from 'Components/select'
import Breadcrumb from 'Components/breadcrumb'
import FooterStatement from 'Components/footer-statement'
import { sendTeaCommEvent } from 'lib/tea-analyze'

import {
  fetchList,
  fetchSeriesInfo,
  fetchCategoryList,
  fetchCategoryImageCount,
} from './request'
import { IData, IImgDetail } from './interface'
import { CategoryCarList, CarInfo } from '../list/home'

import { isNewVersion } from 'lib/version'
import Head from 'Components/head'
import serializeObject from '@arsenal/arsenal/modules/serializeObject'
import Link from 'next/link'

interface ICacheData {
  offsets: number[]
  data: IData
}

function getQueryParams<T>(query: T | T[]) {
  return Array.isArray(query) ? query[query.length - 1] : query
}

export const count = 30

// 数据缓存
const cacheData: Map<string, ICacheData> = new Map()
// let first = true
// let hasSendRequest = false
let categoryList: IList[]
let initCarId: number
let initIndex: number
let initColor: string

const imageCountCache: Record<string, any> = {}
const listDataCache: Record<string, any> = {}

let prevSeriesId: string
let prevSeriesInfo: any
let prevCategoryList: any

function ImageDetail(props: IImgDetail) {
  const {
    seriesId,
    seriesInfo,
    initCategory,
    carId: queryCarId,
    index: queryIndex,
    color: queryColor,
    listDataKey,
    imageCountKey,
    carsData,
    categoryListData,
    asPath,
    imageCount,
  } = props

  prevSeriesId = seriesId
  prevSeriesInfo = seriesInfo
  prevCategoryList = categoryListData

  imageCountCache[imageCountKey] = imageCount
  listDataCache[listDataKey] = carsData

  categoryList = categoryListData || []

  // 获取当前特性下标
  let defaultCurrent = 0
  for (const index in categoryList) {
    if (
      get(categoryList, `${[index]}.key`) === initCategory ||
      get(categoryList, `${[index]}.text`) === initCategory
    ) {
      defaultCurrent = parseInt(index, 10)
      break
    }
  }

  // 获取车型列表
  const defaultList = get(categoryList[defaultCurrent], 'filter.car', []).map(
    (item: CategoryCarList) => {
      if (!item.car_list.length) {
        return { value: '', text: item.category_text }
      } else {
        return {
          label: item.category_text,
          // eslint-disable-next-line max-nested-callbacks
          options: item.car_list.map((carItem: CarInfo) => ({
            value: carItem.car_id,
            text: carItem.car_text,
          })),
        }
      }
    }
  )

  initCarId = Number(getQueryParams(queryCarId)) || 0
  initIndex = Number(getQueryParams(queryIndex)) || 0
  initColor = getQueryParams(queryColor !== 'x' && queryColor ? queryColor : '')

  const car_name = useMemo(() => {
    const carInfo = carsData?.list?.find(
      (ele) => String(ele?.info?.car_id) === String(initCarId)
    )
    if (carInfo) {
      return `${carInfo?.info?.year ? `${carInfo?.info?.year}年款` : ''} ${
        carInfo?.info?.car_name
      }`
        ?.split(' ')
        ?.join('')
    }
    return ''
  }, [carsData, initCarId])

  const color_name = useMemo(() => {
    const name = carsData?.list.find(
      (ele) =>
        String(ele.info?.color)?.toUpperCase() ===
        `#${initColor.split('_')[0]}`?.toUpperCase()
    )?.info?.color_name
    return `${name || ''}`
  }, [carsData, initColor])

  const categoryText = useMemo(() => {
    return categoryListData?.find((ele) => ele.key === initCategory)?.text
  }, [categoryListData])

  const renderHeaderTDK = () => {
    const brandName = seriesInfo?.brand_name
    const seriesName = seriesInfo?.name
    const pageIndex = initIndex

    // 1、默认有颜色：品牌_车系名_车系名外观图片_车系名+颜色整车外观图片_懂车帝
    // 2、默认无颜色：品牌_车系名_车系名外观图片_懂车帝"
    let title = `${brandName}_${seriesName}_${seriesName}${categoryText}图片${
      color_name ? `_${seriesName}${color_name}整车${categoryText}图片` : ''
    }_懂车帝`
    let keywords = `${brandName},${seriesName},${seriesName}${categoryText}图片${
      color_name ? `,${seriesName}${color_name}整车${categoryText}图片` : ''
    }`
    let description = `懂车帝提供最新最全的${brandName}${seriesName}汽车图片大全，包括${brandName}${seriesName}${color_name}${categoryText}图片细节等，想了解更多${brandName}${seriesName}汽车图片尽在懂车帝。`

    if (pageIndex > 0) {
      title = `${brandName}_${seriesName}_${seriesName}${categoryText}图片_${pageIndex}/${
        imageCount?.[initCategory]
      }张图片_${
        color_name ? `_${seriesName}${color_name}整车${categoryText}图片` : ''
      }_懂车帝`
      description += `您当前浏览的是第${pageIndex}张${seriesName}${car_name}高清汽车图片。`
    }

    if (car_name) {
      title = `${seriesName}${car_name}${color_name}图片_${brandName}_汽车${categoryText}图库_懂车帝`
      keywords = `${seriesName}${car_name}${color_name}图片,${brandName},${seriesName},汽车${categoryText}图库`
      description = `懂车帝为您提供${seriesName}${car_name}${color_name}高清汽车图片大全，涵盖车身外观、颜色等图片。看${seriesName}${car_name}相关汽车图片，就上懂车帝。`
      if (pageIndex > 0) {
        title = `${seriesName}${car_name}图片_${brandName}_${pageIndex}/${imageCount?.[initCategory]}张图片_汽车${categoryText}图库_懂车帝`
        description += `您当前浏览的是第${pageIndex}张${seriesName}${car_name}高清汽车图片。`
      }
    }

    // 选择车型
    return (
      <Head
        title={title}
        keywords={keywords}
        description={description}
        canonical={`https://www.dongchedi.com${asPath}`}
      />
    )
  }

  const imgAltName = useMemo(() => {
    let result = `${seriesInfo?.brand_name}${seriesInfo?.name}${color_name}${categoryText}图片`
    if (car_name) {
      // 车系名+车型名+颜色图片
      result = `${seriesInfo?.name}${car_name}${color_name}${categoryText}图片`
    }
    return result
  }, [
    seriesInfo?.brand_name,
    seriesInfo?.name,
    categoryText,
    color_name,
    car_name,
  ])

  return (
    <>
      {renderHeaderTDK()}
      <div className="image-detail">
        <div className="body-container">
          <header>
            <div>
              <Breadcrumb
                path={[
                  { name: '首页', href: '/home', as: '/' },
                  { name: seriesInfo?.brand_name, href: '/auto', as: '/auto' },
                  {
                    name: seriesInfo?.name,
                    href: `/auto/series?seriesId=${seriesId}`,
                    as: `/auto/series/${seriesId}`,
                  },
                  {
                    name: '实拍大图',
                    href: `/auto/images/list?seriesId=${seriesId}`,
                    as: `/auto/series/${seriesId}/images`,
                  },
                  { name: '图片详情' },
                ]}
                className={'inverse-color'}
                hideLine
              />
            </div>
            <Select
              className="search-select"
              value={initCarId}
              color={initColor}
              category={initCategory}
              seriesId={seriesId}
              defaultText="不限车型"
              options={defaultList}
              onChange={({ value, text }) => {
                sendTeaCommEvent('click_series_style_filter', {
                  car_series_id: seriesId,
                  car_style_id: value,
                  car_style_name: text,
                })
              }}
              renderLink={(value) => (content) =>
                (
                  <Link
                    href={`/auto/images/detail`}
                    as={`/series-${seriesId}/images/${initCategory || 'x'}-${
                      value || 'x'
                    }-x-x`}
                  >
                    <a>{content}</a>
                  </Link>
                )}
            />
          </header>
          <section>
            <ImageRidingLantern
              data={carsData as IData}
              initIndex={initIndex}
              seriesId={seriesId}
              carId={initCarId}
              color={initColor}
              category={initCategory}
              imgAltName={imgAltName}
            />
            <Aside
              price={carsData?.list?.[initIndex % 30]?.info?.price}
              seriesId={seriesId}
              carId={initCarId}
              color={initColor}
              category={initCategory}
              list={categoryList}
              categoryImages={props?.imageCount}
            />
          </section>
        </div>
        <footer>
          <FooterStatement />
        </footer>
      </div>
      <style jsx>
        {`
          .image-detail {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            align-items: center;
            min-width: 1200px;
            width: 100%;
            min-height: 100vh;
            font-family: PingFangSC;
            background-color: #000000;
          }
          .body-container {
            width: 1190px;
            height: 100%;
            margin-bottom: 80px;
          }
          header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            height: 60px;
            margin-right: 122px;
          }
          section {
            display: flex;
            justify-content: space-between;
          }
          footer {
            width: 100%;
            align-self: flex-end;
          }
          header :global(.search-select) {
            text-align: left;
            width: 340px;
            height: 36px;
            font-size: 14px;
          }
          header :global(.search-select .text) {
            color: #fff;
          }
          header :global(.search-select .list) {
            color: #1a1a1a;
          }
          header :global(.icon-up-arrow-ic) {
            color: #fff;
          }
          .image-detail .statement {
            background: rgba(248, 248, 248, 0.1);
          }
          .image-detail .hide-line,
          .image-detail .hide-line span {
            color: #fff;
          }
        `}
      </style>
    </>
  )
}

ImageDetail.getInitialProps = async ({ query, req, asPath }: any) => {
  const params =
    asPath.match(/series-(\d+)\/images\/([^-]+)-([^-]+)-([^-]+)-([^-]+)/) || []

  const category = params[2] === 'x' || !params[2] ? 'wg' : params[2]
  const carId = Number(params[3]) || 0
  const index = Number(params[5]) || 0
  const color = params[4] !== 'x' && params[4] ? params[4] : ''
  const seriesId = params[1]

  const fetchListParams = {
    seriesId,
    color,
    offset: Math.floor(index / count) * count,
    category,
    carId,
  }

  const listDataKey = serializeObject(fetchListParams)

  try {
    const data = (
      await Promise.allSettled([
        !req && prevSeriesId === seriesId && prevSeriesInfo
          ? Promise.resolve(prevSeriesInfo)
          : fetchSeriesInfo(seriesId, req),
        listDataCache[listDataKey]
          ? Promise.resolve(listDataCache[listDataKey])
          : fetchList(fetchListParams),
        !req && prevSeriesId === seriesId && prevCategoryList
          ? Promise.resolve(prevCategoryList)
          : fetchCategoryList(seriesId),
      ])
    ).map((d) => (d.status === 'fulfilled' ? d.value : null))

    const imageCountParams = {
      seriesId,
      carId,
      category: data?.[2]?.map((c: any) => c.key).join(','),
    }

    const imageCountKey = serializeObject(imageCountParams)

    const imageCount =
      imageCountCache[imageCountKey] ||
      (await fetchCategoryImageCount(imageCountParams))?.data

    return {
      seriesId,
      category,
      carId,
      color,
      index,
      listDataKey,
      imageCountKey,
      seriesInfo: data?.[0],
      carsData: data?.[1],
      categoryListData: data?.[2],
      imageCount,
      initCategory: category,
      asPath,
      _pageType: isNewVersion(req?._isNewVersion) ? 'full auto-nav' : '',
    }
  } catch (error) {
    console.error('[Series.getInitialProps]', error)
    return {
      ...query,
      initCategory: category,
      asPath,
      _pageType: isNewVersion(req?._isNewVersion) ? 'full auto-nav' : '',
    }
  }
}

export default ImageDetail
